import React, { useState, useEffect, useRef } from 'react';
import styles from './AdminChatbotWidget.module.css';
import { AdminChatbotService, ChatMessage, ChatHistoryItem } from '../../../services/ai/adminChatbotService';

const STORAGE_KEY = 'jejak_admin_chat_history_v1';

const DEFAULT_WELCOME_MESSAGE: ChatMessage = {
  sender: 'bot',
  text: `🚧 **INFORMASI FITUR AI** 🚧

Pengembangan **Jejak Admin AI Assistant** saat ini ditunda sementara.

Fitur ini masih dalam tahap pengembangan (*Under Development*) dan belum diaktifkan untuk penggunaan umum. Terima kasih atas pengertian Anda!`,
  timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
};

const getInitialMessages = (): ChatMessage[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (err) {
    console.error('Failed to load admin chat history:', err);
  }
  return [DEFAULT_WELCOME_MESSAGE];
};

export const AdminChatbotWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<ChatMessage[]>(getInitialMessages);
  const [inputValue, setInputValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (err) {
      console.error('Failed to save admin chat history:', err);
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, isLoading]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClearChat = () => {
    if (window.confirm('Apakah Anda yakin ingin menghapus riwayat percakapan AI Chatbot?')) {
      setMessages([DEFAULT_WELCOME_MESSAGE]);
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const handleSendMessage = async (customText?: string) => {
    const query = (customText || inputValue).trim();
    if (!query || isLoading) return;

    const userMsg: ChatMessage = {
      sender: 'user',
      text: query,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMsg]);
    if (!customText) setInputValue('');
    setIsLoading(true);

    const history: ChatHistoryItem[] = messages.slice(-10).map((m) => ({
      role: m.sender === 'user' ? 'user' : 'model',
      content: m.text
    }));

    try {
      const botResponse = await AdminChatbotService.sendMessage(query, history);
      setMessages((prev) => [...prev, botResponse]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: 'Terjadi kesalahan sistem saat menghubungi AI Assistant.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Basic Markdown Renderer for Chat Message
  const renderFormattedContent = (content: string) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let inTable = false;
    let tableHeader: string[] = [];
    let tableRows: string[][] = [];

    const flushTable = (keyIndex: number) => {
      if (tableHeader.length > 0) {
        elements.push(
          <div key={`table-${keyIndex}`} className={styles.chatTableWrapper}>
            <table className={styles.chatTable}>
              <thead>
                <tr>
                  {tableHeader.map((h, i) => (
                    <th key={i}>{h.trim()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx}>{cell.trim()}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      tableHeader = [];
      tableRows = [];
      inTable = false;
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Check Markdown Table
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        const cells = trimmed.split('|').slice(1, -1);
        if (!inTable) {
          inTable = true;
          tableHeader = cells;
        } else if (trimmed.includes('---')) {
          // Separator row, skip
        } else {
          tableRows.push(cells);
        }
        return;
      } else if (inTable) {
        flushTable(index);
      }

      // Formatting text bold & italic
      let formattedText: React.ReactNode = trimmed;
      if (trimmed.length > 0) {
        const parts = trimmed.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`)/g);
        formattedText = parts.map((part, pIdx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={pIdx}>{part.slice(2, -2)}</strong>;
          }
          if (part.startsWith('*') && part.endsWith('*')) {
            return <em key={pIdx}>{part.slice(1, -1)}</em>;
          }
          if (part.startsWith('`') && part.endsWith('`')) {
            return <code key={pIdx} style={{ background: '#e2e8f0', padding: '2px 4px', borderRadius: '4px' }}>{part.slice(1, -1)}</code>;
          }
          return part;
        });
      }

      if (trimmed.startsWith('• ') || trimmed.startsWith('- ')) {
        elements.push(
          <div key={index} style={{ marginLeft: '12px', marginTop: '2px', marginBottom: '2px' }}>
            • {formattedText}
          </div>
        );
      } else if (trimmed === '') {
        elements.push(<div key={index} style={{ height: '6px' }} />);
      } else {
        elements.push(<div key={index}>{formattedText}</div>);
      }
    });

    if (inTable) {
      flushTable(lines.length);
    }

    return elements;
  };

  return (
    <div className={styles.chatbotWidget}>
      {/* Floating Toggle Button */}
      <button
        className={styles.chatbotToggleBtn}
        onClick={handleToggle}
        title="Admin Agentic AI Assistant"
      >
        {isOpen ? '✕' : '🤖'}
      </button>

      {/* Chat Window */}
      <div className={`${styles.chatbotWindow} ${isOpen ? styles.open : ''}`}>
        {/* Header */}
        <div className={styles.chatbotHeader}>
          <div className={styles.headerInfo}>
            <div className={styles.botAvatar}>🤖</div>
            <div>
              <div className={styles.headerTextTitle}>
                Jejak Admin AI
                <span className={styles.headerBadge} style={{ background: '#f59e0b', color: '#0f172a' }}>DEV PAUSED</span>
              </div>
              <p className={styles.headerSubtitle}>Real-time Operational & Analytics Assistant</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.headerActionBtn}
              onClick={handleClearChat}
              title="Hapus Percakapan"
            >
              🗑️
            </button>
            <button
              className={styles.headerActionBtn}
              onClick={handleToggle}
              title="Tutup"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Development Notice Banner */}
        <div style={{
          backgroundColor: '#fffbe6',
          color: '#d46b08',
          borderBottom: '1px solid #ffe58f',
          padding: '10px 16px',
          fontSize: '12px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          lineHeight: '1.4'
        }}>
          <span style={{ fontSize: '18px' }}>🚧</span>
          <div>
            <div style={{ fontWeight: 700, color: '#ad6800', fontSize: '13px' }}>Pengembangan Ditunda (Under Development)</div>
            <div style={{ fontSize: '11px', fontWeight: 400, color: '#8c5000', marginTop: '2px' }}>
              Fitur AI ini masih dalam tahap pengembangan dan belum dapat digunakan.
            </div>
          </div>
        </div>

        {/* Chat Messages Body */}
        <div className={styles.chatbotBody}>
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`${styles.messageRow} ${msg.sender === 'user' ? styles.user : styles.bot}`}
            >
              <div className={styles.messageBubble}>
                {renderFormattedContent(msg.text)}
                <div className={styles.timestamp}>
                  {msg.timestamp} {msg.modelUsed ? `• ${msg.modelUsed}` : ''}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className={`${styles.messageRow} ${styles.bot}`}>
              <div className={styles.messageBubble}>
                <div className={styles.typingIndicator}>
                  <div className={styles.dot} />
                  <div className={styles.dot} />
                  <div className={styles.dot} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Prompts */}
        <div className={styles.quickPrompts}>
          <button
            className={styles.promptChip}
            style={{ opacity: 0.6, cursor: 'not-allowed' }}
            disabled={true}
          >
            📦 Order Aktif
          </button>
          <button
            className={styles.promptChip}
            style={{ opacity: 0.6, cursor: 'not-allowed' }}
            disabled={true}
          >
            💰 Invoice Unpaid
          </button>
          <button
            className={styles.promptChip}
            style={{ opacity: 0.6, cursor: 'not-allowed' }}
            disabled={true}
          >
            🚢 Cek Kontainer
          </button>
        </div>

        {/* Footer Input */}
        <div className={styles.chatbotFooter}>
          <input
            type="text"
            className={styles.inputField}
            placeholder="Fitur ini masih dalam tahap pengembangan..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={true}
          />
          <button
            className={styles.sendBtn}
            onClick={() => handleSendMessage()}
            disabled={true}
          >
            ➔
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminChatbotWidget;
